/**
 * "Set up your Kita": which of the first-day settings an organisation still
 * lacks, read from endpoints that already exist. Each check asks for the
 * smallest page that answers yes or no, or reads an answer the Dashboard
 * already holds (the location and group lists, the settings).
 */

import { useEffect, useMemo } from 'react';
import { useQueries, type Query, type QueryKey } from '@tanstack/react-query';
import { useAuth } from '@clerk/clerk-react';
import { useAuthContext } from '@/contexts/AuthContext';
import { OrganisationSettingsService } from '@/api/services/OrganisationSettingsService';
import { GroupsService } from '@/api/services/GroupsService';
import { TariffsService } from '@/api/services/TariffsService';
import { HolidaysService } from '@/api/services/HolidaysService';
import { AccessRolesService } from '@/api/services/AccessRolesService';
import { PUBLIC_HOLIDAY_TYPE } from '@/types/publicHoliday';
import { retryUnlessClientError } from '@/lib/queryRetry';
import { capacitySettingsKeys } from '@/hooks/useCapacitySettings';
import { billingKeys } from '@/hooks/useBilling';
import { groupsKeys } from '@/hooks/settings/useGroups';

type CapacitySettingsAnswer = Awaited<ReturnType<typeof OrganisationSettingsService.getApiV1OrganisationsCapacitySettings>>;
type BillingSettingsAnswer = Awaited<ReturnType<typeof OrganisationSettingsService.getApiV1OrganisationsBillingSettings>>;
type ChildcaresAnswer = Awaited<ReturnType<typeof OrganisationSettingsService.getApiV1OrganisationsChildcares>>;
type GroupsAnswer = Awaited<ReturnType<typeof GroupsService.getApiV1Groups>>;

/** The page of the shared location and group lists the Dashboard's filter reads (`useChildcares`, `useGroups`). */
const SHARED_LIST_LIMIT = 100;

export type SetupItemId =
  | 'childcare'
  | 'group'
  | 'staffingModel'
  | 'qrIban'
  | 'tariff'
  | 'holidays'
  | 'employeeRole';

/** `unknown` while loading or after a failed read: never shown as open. */
export type SetupItemState = 'done' | 'open' | 'unknown';

export interface SetupItem {
  id: SetupItemId;
  /** The page where the user does this step. */
  href: string;
  state: SetupItemState;
}

export interface SetupChecklist {
  items: SetupItem[];
  doneCount: number;
  /** At least one step is known to be missing. */
  hasOpen: boolean;
  /** Every step is done (or was done on an earlier visit). */
  isComplete: boolean;
}

/** In the order the setup has to happen: a group needs a childcare, a tariff needs the billing settings. */
export const SETUP_ITEMS: ReadonlyArray<{ id: SetupItemId; href: string }> = [
  { id: 'childcare', href: '/settings/childcare' },
  { id: 'group', href: '/settings/groups' },
  { id: 'staffingModel', href: '/settings/capacity' },
  { id: 'qrIban', href: '/settings/finance' },
  { id: 'tariff', href: '/contracts?tab=tariffs' },
  { id: 'holidays', href: '/settings/public-holidays' },
  { id: 'employeeRole', href: '/employees' },
];

const HOLIDAY_COUNTRY = 'CH';

/** A step's read: its own yes/no answer, or a shared answer it reads the step from. */
interface SetupSource<T> {
  queryKey: (orgId: string) => QueryKey;
  read: (orgId: string) => Promise<T>;
  isDone(answer: T): boolean;
}

/** A step answered by its own yes/no read. */
function check(id: SetupItemId, read: (orgId: string) => Promise<boolean>): SetupSource<boolean> {
  return { queryKey: (orgId) => setupChecklistKeys.item(orgId, id), read, isDone: (done) => done };
}

const SOURCES: Record<
  SetupItemId,
  | SetupSource<boolean>
  | SetupSource<CapacitySettingsAnswer>
  | SetupSource<BillingSettingsAnswer>
  | SetupSource<ChildcaresAnswer>
  | SetupSource<GroupsAnswer>
> = {
  // Read from the location and group lists the Dashboard's filter holds, so the
  // Dashboard asks once (#2193 A).
  childcare: {
    queryKey: (orgId) => ['childcares', orgId, { limit: SHARED_LIST_LIMIT, offset: 0 }],
    read: (orgId) => OrganisationSettingsService.getApiV1OrganisationsChildcares(orgId, SHARED_LIST_LIMIT, 0),
    isDone: (page: ChildcaresAnswer) =>
      (page.totalCount ?? (Array.isArray(page.data) ? page.data.length : 0)) > 0,
  },
  group: {
    queryKey: (orgId) => groupsKeys.list(orgId),
    read: () => GroupsService.getApiV1Groups(SHARED_LIST_LIMIT, 0),
    isDone: (groups: GroupsAnswer) => (groups ?? []).length > 0,
  },
  // False until an admin saves the staffing model once; until then coverage reads
  // NOT_CONFIGURED. Read from the capacity settings entry the group cards use, so
  // the Dashboard asks once (#2193 B4).
  staffingModel: {
    queryKey: (orgId) => capacitySettingsKeys.settings(orgId),
    read: (orgId) => OrganisationSettingsService.getApiV1OrganisationsCapacitySettings(orgId),
    isDone: (settings: CapacitySettingsAnswer) => !!settings.staffing_requirements_configured,
  },
  // The QR bill (PDF and e-mail) refuses to render without it. Read from the
  // billing settings entry useBillingSettings uses, so the Dashboard asks once (#2193 B4).
  qrIban: {
    queryKey: (orgId) => billingKeys.settings(orgId),
    read: (orgId) => OrganisationSettingsService.getApiV1OrganisationsBillingSettings(orgId),
    isDone: (settings: BillingSettingsAnswer) => !!settings.qrIban?.trim(),
  },
  // Only an ACTIVE version bills; a draft or an archived one does not count.
  tariff: check('tariff', async () => {
    const page = await TariffsService.getApiV1TariffVersions(
      undefined,
      'ACTIVE',
      undefined,
      false,
      undefined,
      'created_at',
      'desc',
      1,
      0,
    );
    return (page.total_count ?? page.data?.length ?? 0) > 0;
  }),
  holidays: check('holidays', async () => {
    const list = await HolidaysService.getApiV1HolidaysCountry(HOLIDAY_COUNTRY);
    return (list.holidays ?? []).some((h) => h.holidayType === PUBLIC_HOLIDAY_TYPE);
  }),
  // employeeCount counts employees whose work contract covering today carries the role.
  employeeRole: check('employeeRole', async () =>
    (await AccessRolesService.getApiV1AccessRoles()).some((role) => role.employeeCount > 0),
  ),
};

/**
 * An open step is re-read after 15 s, so returning from a settings page shows
 * it ticked; a done step does not come undone and stays fresh for 10 minutes
 * (#2193 B: the Dashboard stops re-asking on every visit).
 */
const OPEN_STEP_STALE_MS = 15 * 1000;
const DONE_STEP_STALE_MS = 10 * 60 * 1000;

export const setupChecklistKeys = {
  all: ['setupChecklist'] as const,
  item: (orgId: string | null | undefined, id: SetupItemId) =>
    [...setupChecklistKeys.all, orgId ?? null, id] as const,
};

const COMPLETE_STORAGE_PREFIX = 'daycare-setup-complete.';

// A finished setup does not come undone, so once every step was seen done the
// dashboard stops asking. Storage can be missing or throw; then it asks again.
function readRememberedComplete(orgId: string): boolean {
  try {
    return window.localStorage.getItem(COMPLETE_STORAGE_PREFIX + orgId) === '1';
  } catch {
    return false;
  }
}

function rememberComplete(orgId: string): void {
  try {
    window.localStorage.setItem(COMPLETE_STORAGE_PREFIX + orgId, '1');
  } catch {
    // Without storage the checks simply run again on the next visit.
  }
}

/**
 * The setup steps of the current organisation and whether each is done.
 *
 * @param options.enabled - Gate the reads; the card is for admins only
 */
export function useSetupChecklist({ enabled }: { enabled: boolean }): SetupChecklist {
  const { isSignedIn } = useAuth();
  const { isAuthReady, orgId } = useAuthContext();
  const remembered = useMemo(() => (orgId ? readRememberedComplete(orgId) : false), [orgId]);
  const active = enabled && !!isSignedIn && isAuthReady && !!orgId && !remembered;

  const results = useQueries({
    queries: SETUP_ITEMS.map((item) => {
      const source: SetupSource<unknown> = SOURCES[item.id];
      return {
        queryKey: source.queryKey(orgId ?? ''),
        queryFn: () => source.read(orgId as string),
        select: source.isDone,
        enabled: active,
        staleTime: (query: Query) =>
          query.state.data !== undefined && source.isDone(query.state.data) ? DONE_STEP_STALE_MS : OPEN_STEP_STALE_MS,
        retry: retryUnlessClientError,
      };
    }),
  });

  const items: SetupItem[] = SETUP_ITEMS.map((item, index) => {
    const result = results[index];
    let state: SetupItemState = 'unknown';
    if (remembered) state = 'done';
    else if (result?.isSuccess) state = result.data ? 'done' : 'open';
    return { ...item, state };
  });

  const doneCount = items.filter((item) => item.state === 'done').length;
  const isComplete = doneCount === items.length;
  const hasOpen = items.some((item) => item.state === 'open');

  useEffect(() => {
    if (active && isComplete && orgId) rememberComplete(orgId);
  }, [active, isComplete, orgId]);

  return { items, doneCount, hasOpen, isComplete };
}
